import { BackendType, FinancingPlan } from "../models/financing";
import { IAPR, IMonths, isoAPR, isoMonths } from "../models/nominals";
import { Dinero, getDinero } from "./money";
import { getPageSetting } from "./settings";

export interface FinancingPlanMeta {
    type?: string;
    threshold: Dinero;
    length: IMonths;
    apr: IAPR;
    superscript: string;
    description: string;
    default?: boolean;
}

/**
 * List the currently available Financing financing plans.
 */
export const listFinancingPlans = (): FinancingPlanMeta[] => {
    const plans = (getPageSetting("financing_advertising_plans") || []).map(
        (plan): FinancingPlanMeta => {
            return {
                type: plan.backend_type.code,
                threshold: getDinero(plan.product_price_threshold),
                length: isoMonths.wrap(plan.term_months),
                apr: isoAPR.wrap(plan.apr),
                superscript: plan.fine_print_superscript,
                description: plan.description,
                default: plan.is_default_plan,
            };
        },
    );
    plans.sort((a, b) => {
        if (a.threshold === b.threshold) {
            return 0;
        }
        return a.threshold < b.threshold ? -1 : 1;
    });
    return plans;
};

/**
 * List filtered Financing plans.
 */
const listFilteredFinancingPlans = (
    backendType: BackendType,
): FinancingPlanMeta[] => {
    const plans = listFinancingPlans();
    const filteredPlans = plans.filter(
        (plan) => plan.type?.toLowerCase() === backendType,
    );
    return filteredPlans;
};

export const getDefaultFinancingPlan = (): FinancingPlanMeta | null => {
    const plans = listFinancingPlans();
    return plans.find((plan) => plan.default) ?? null;
};

export const getLowestThresholdPlan = (): FinancingPlanMeta | null => {
    const plans = listFinancingPlans();
    return plans.length > 0 ? plans[0] : null;
};

export const getLowestThresholdFortivaPlan = (
    backendType: BackendType,
): FinancingPlanMeta | null => {
    const filteredPlans = listFilteredFinancingPlans(backendType);
    return filteredPlans.length > 0 ? filteredPlans[0] : null;
};

export const getBestEligiblePlan = (
    amount: Dinero,
): FinancingPlanMeta | null => {
    const plans = listFinancingPlans().filter((plan) => {
        return plan.threshold.lessThanOrEqual(amount);
    });
    plans.sort((a, b) => {
        if (a.length === b.length) {
            return 0;
        }
        return a.length < b.length ? 1 : -1;
    });
    return plans.length > 0 ? plans[0] : null;
};

export const getLongestActivePlan = async (
    financingPlans: readonly FinancingPlan[],
): Promise<FinancingPlanMeta | null> => {
    // Make sure to load them properly to avoid indirectly triggering an uninitialized import
    const { filterFinancingPlans, getLongestFinancingPlan } = await import(
        "../apps/checkout/utils"
    );
    if (financingPlans) {
        // Before retrieving the longest active financing plan, narrow down the selection to include only plans with a 0% APR
        const filteredFinancingPlans = filterFinancingPlans(financingPlans);
        const longestPlan = getLongestFinancingPlan(filteredFinancingPlans);
        return longestPlan && isoMonths.unwrap(longestPlan.term_months) > 0
            ? {
                  threshold: getDinero(
                      longestPlan.product_price_threshold || "0",
                  ),
                  length: longestPlan.term_months,
                  apr: longestPlan.apr || isoAPR.wrap("0"),
                  superscript: longestPlan.fine_print_superscript || "",
                  description: longestPlan.description || "",
              }
            : null;
    }
    return null;
};
